import { menuLeaves, menuStage, SETUP_CHANGES_PATH } from '@felix/ui';
import { Center, Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router';
import type { SessionStage } from './api.js';
import { AddVoyagePage } from './pages/AddVoyagePage.js';
import { AllReportsPage } from './pages/AllReportsPage.js';
import { CashPage } from './pages/CashPages.js';
import { CertificatesPage } from './pages/CertificatesPage.js';
import { CharterPartyPage } from './pages/CharterPartyPage.js';
import { CharterPartyRegisterPage } from './pages/CharterPartyRegisterPage.js';
import { ComingSoonPage } from './pages/ComingSoonPage.js';
import { CrewListsPage } from './pages/CrewListsPage.js';
import { CrewPage, CrewProfilePage, LoginAccountsPage, SignOnOffPage } from './pages/CrewPage.js';
import { DailyPlanPage, DailyReportsPage } from './pages/DailyPages.js';
import { DashboardPage } from './pages/DashboardPage.js';
import {
  DistributionPage,
  FormCategoriesPage,
  IsmFormsPage,
  ManualsFoldersPage,
  ManualsPage,
} from './pages/DocumentPages.js';
import {
  DrillAnalyticsPage,
  DrillCompetencyPage,
  DrillDashboardPage,
  DrillLibraryPage,
  DrillPlannerPage,
  DrillRecordsPage,
  TrainingNeedsPage,
} from './pages/DrillPages.js';
import {
  DrydockFleetPage,
  DrydockInspectionPage,
  DrydockJobsPage,
  DrydockLibraryPage,
  DrydockProjectsPage,
} from './pages/DrydockPages.js';
import { FleetPage } from './pages/FleetPage.js';
import { FormHistoryPage, FormRecordPage } from './pages/FormRecordPages.js';
import { FuelPage } from './pages/FuelPages.js';
import { HsePage } from './pages/HsePages.js';
import { LegEventPage } from './pages/LegEventPage.js';
import { LoginPage } from './pages/LoginPage.js';
import {
  MaterialMasterPage,
  ProvisionPage,
  StockInOutPage,
  StockManagementPage,
  VictuallingPage,
} from './pages/MaterialPages.js';
import { NoonReportPage } from './pages/NoonReportPage.js';
import { PasswordPage } from './pages/PasswordPage.js';
import { PayrollPage } from './pages/PayrollPages.js';
import { PmsEquipmentPage } from './pages/PmsEquipmentPage.js';
import {
  PmsClassIntegrationPage,
  PmsDefectsPage,
  PmsHistoryPage,
  PmsLibraryPage,
  PmsPlanPage,
  PmsPostponementsPage,
  PmsWorkOrdersPage,
  SetupChangesPage,
} from './pages/PmsPages.js';
import { PortCallsPage } from './pages/PortCallsPage.js';
import { PortDistancesPage, RoutePage } from './pages/PortPages.js';
import {
  GoodsReceiptsPage,
  PurchaseOrdersPage,
  ReportsPage,
  RequisitionsPage,
  RfqPage,
  SuppliersPage,
} from './pages/PurchasingPages.js';
import { RestHoursPage } from './pages/RestHoursPage.js';
import {
  RiskAssessmentPage,
  RiskDashboardPage,
  RiskFindingsPage,
  RiskLibraryPage,
  RiskMasterDataPage,
  RiskRegisterPage,
  RiskReportsPage,
  RiskVerificationPage,
} from './pages/RiskPages.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { MatricesPage, RolesPage, SetupWizardPage, UsersPage } from './pages/SetupPages.js';
import { ShipClockPage } from './pages/ShipClockPage.js';
import { TotpPage } from './pages/TotpPage.js';
import { TotpSetupPage } from './pages/TotpSetupPage.js';
import { VesselCertificatesPage } from './pages/VesselCertificatesPage.js';
import { VoyageListPage } from './pages/VoyageListPage.js';
import { VoyagePerformancePage } from './pages/VoyagePerformancePage.js';
import {
  EventTypesPage,
  FuelAnalysisPage,
  ReportTemplatesPage,
  VoyageDetailsPage,
  VoyageSummaryPage,
  VoyageTimelinePage,
} from './pages/VoyageRecordPages.js';
import { PATHS, pathForStage } from './routing.js';
import { useSession } from './session.js';

/** Ekran yalnız kendi oturum aşamasında açılır; değilse aşamanın ekranına yönlenir. */
function Guard({
  stage,
  children,
}: {
  /** Sayfanın açıldığı oturum aşaması ya da aşamaları. */
  stage: SessionStage | null | readonly (SessionStage | null)[];
  children: ReactNode;
}) {
  const session = useSession();
  const { t } = useTranslation();
  if (session.stage === undefined) {
    return (
      <Center h="100dvh">
        <Loader aria-label={t('common.loading')} />
      </Center>
    );
  }
  const allowed: readonly (SessionStage | null)[] = Array.isArray(stage) ? stage : [stage];
  if (!allowed.includes(session.stage)) {
    return <Navigate to={pathForStage(session.stage)} replace />;
  }
  return <>{children}</>;
}

const PASSWORD_STAGES = ['password_change_required', 'active'] as const;

/** Hazır ekranlar; menüdeki diğer öğeler "hazırlanıyor" sayfası açar (ADR 0019 §3). */
const SCREENS: Record<string, () => ReactNode> = {
  dashboard: () => <DashboardPage />,
  fleetVessels: () => <FleetPage />,
  crewList: () => <CrewPage />,
  // Crew List IMO FAL Form 5 (ADR 0130 §B)
  crewLists: () => <CrewListsPage />,
  // Joining / Sign-Off (ADR 0131 §3)
  crewSignOnOff: () => <SignOnOffPage />,
  // Giriş hesapları (ADR 0147): yalnız ofis
  crewLoginAccounts: () => <LoginAccountsPage />,
  crewCertificates: () => <CertificatesPage />,
  crewRestHours: () => <RestHoursPage />,
  // Gemi saati kaydı (M18; ADR 0065): salt okunur
  crewShipClock: () => <ShipClockPage />,
  voyageNoonReport: () => <NoonReportPage />,
  // All Reports ve dokuz rapor öğesi (ADR 0082): tür süzgeci menü yolundan
  voyageReportsAll: () => <AllReportsPage />,
  voyageReportsArrival: () => <AllReportsPage />,
  voyageReportsDailyPort: () => <AllReportsPage />,
  voyageReportsDeparture: () => <AllReportsPage />,
  voyageReportsAnchoring: () => <AllReportsPage />,
  voyageReportsAnchorAweigh: () => <AllReportsPage />,
  voyageReportsDrifting: () => <AllReportsPage />,
  voyageReportsDriftDeparture: () => <AllReportsPage />,
  voyageReportsCanalStrait: () => <AllReportsPage />,
  voyageReportsNoon: () => <AllReportsPage />,
  voyageList: () => <VoyageListPage />,
  voyageNew: () => <AddVoyagePage />,
  // Sefer kaydı, çizelge, özet ve Reference Data (ADR 0082 Ek): raporlardan türetilir
  voyageDetails: () => <VoyageDetailsPage />,
  voyageTimeline: () => <VoyageTimelinePage />,
  voyageSummary: () => <VoyageSummaryPage />,
  voyageReportTemplates: () => <ReportTemplatesPage />,
  voyageEventTypes: () => <EventTypesPage />,
  // Fuel & Consumption Analysis (V6; ADR 0090)
  voyageFuelAnalysis: () => <FuelAnalysisPage />,
  voyageLegs: () => <LegEventPage />,
  // Limanlar ve mesafeler (M13b; ADR 0048)
  voyageRoute: () => <RoutePage />,
  voyageDistances: () => <PortDistancesPage />,
  // Liman aramaları (M13f; ADR 0052)
  operationsPortCalls: () => <PortCallsPage />,
  // Mürettebat bordrosu (M16; ADR 0061): salt okunur; yetkisiz kişide menü gizli
  crewPayrollMonthly: () => <PayrollPage section="monthly" />,
  crewPayrollAdvances: () => <PayrollPage section="advances" />,
  crewPayrollAllotments: () => <PayrollPage section="allotments" />,
  crewPayrollPayslips: () => <PayrollPage section="payslips" />,
  crewPayrollPayments: () => <PayrollPage section="payments" />,
  crewPayrollClosing: () => <PayrollPage section="closing" />,
  crewPayrollReports: () => <PayrollPage section="reports" />,
  // Kaptan kasası ve Cash to Master (ADR 0093): yetkisiz kişide menü gizli
  financeCashAccounts: () => <CashPage section="accounts" />,
  financeCtmRequests: () => <CashPage section="ctm-requests" />,
  financeCtmApprovals: () => <CashPage section="ctm-approvals" />,
  financeCtmReceipts: () => <CashPage section="ctm-receipts" />,
  financeCtmHistory: () => <CashPage section="ctm-history" />,
  financeCashTransactions: () => <CashPage section="transactions" />,
  financeCashBalances: () => <CashPage section="balances" />,
  financeCashReconciliation: () => <CashPage section="reconciliation" />,
  financeCashMonthly: () => <CashPage section="monthly" />,
  financeReports: () => <CashPage section="reports" />,
  voyagePerformance: () => <VoyagePerformancePage />,
  voyageCharterParty: () => <CharterPartyPage />,
  voyageCharterParties: () => <CharterPartyRegisterPage />,
  materialMaster: () => <MaterialMasterPage />,
  purchasingRequisitions: () => <RequisitionsPage />,
  purchasingRfq: () => <RfqPage />,
  purchasingOrders: () => <PurchaseOrdersPage />,
  purchasingSuppliers: () => <SuppliersPage />,
  purchasingGrn: () => <GoodsReceiptsPage />,
  purchasingStock: () => <StockManagementPage />,
  // Spare Parts (ADR 0122 §5): kumanya dışı stok; eski yol aynı sayfayı açar
  inventorySpareParts: () => <StockInOutPage />,
  inventoryStock: () => <StockInOutPage />,
  // Kumanya (ADR 0099)
  inventoryProvision: () => <ProvisionPage />,
  // Aylık iaşe raporu (ADR 0123)
  inventoryVictualling: () => <VictuallingPage />,
  // Yakıt yönetimi (ADR 0117 §14): yedi sekme `/fuel/<sekme>`
  fuelManagement: () => <FuelPage />,
  pmsEquipment: () => <PmsEquipmentPage />,
  pmsPlan: () => <PmsPlanPage />,
  pmsPostponements: () => <PmsPostponementsPage />,
  pmsWorkOrders: () => <PmsWorkOrdersPage />,
  pmsHistory: () => <PmsHistoryPage />,
  // Arıza DM-17 (M15e; ADR 0060)
  pmsDefects: () => <PmsDefectsPage />,
  // Şirket PMS kütüphanesi (M15f; ADR 0063)
  pmsLibrary: () => <PmsLibraryPage />,
  // DNV MAD / Class Integration (ADR 0132)
  pmsClassIntegration: () => <PmsClassIntegrationPage />,
  // Dry dock ve Pre-Docking Inspection (ADR 0129 Faz 1)
  pmsDrydockProjects: () => <DrydockProjectsPage />,
  pmsDrydockFleet: () => <DrydockFleetPage />,
  pmsDrydockInspection: () => <DrydockInspectionPage />,
  pmsDrydockJobs: () => <DrydockJobsPage />,
  pmsDrydockLibrary: () => <DrydockLibraryPage />,
  // Tatbikat Master Library (ADR 0128 Faz 1); Dashboard ve Planner (Faz 2)
  operationsDrillsDashboard: () => <DrillDashboardPage />,
  operationsDrillsPlanner: () => <DrillPlannerPage />,
  operationsDrillsRecords: () => <DrillRecordsPage />,
  operationsDrillsLibrary: () => <DrillLibraryPage />,
  operationsDrillsCompetency: () => <DrillCompetencyPage />,
  operationsDrillsAnalytics: () => <DrillAnalyticsPage />,
  crewTraining: () => <TrainingNeedsPage />,
  reports: () => <ReportsPage />,
  settings: () => <SettingsPage />,
  // Doküman kütüphanesi (M8a3)
  complianceIsmFormList: () => <IsmFormsPage />,
  complianceManuals: () => <ManualsPage />,
  complianceIsmFormCategories: () => <FormCategoriesPage />,
  // ISM form kayıtları (M8b3; ADR 0042 §21): ofiste salt okunur, ofis adımı
  complianceIsmFormHistory: () => <FormHistoryPage />,
  // Gemi sertifikaları, sörveyler ve klas (M7d)
  complianceVesselCertificates: () => <VesselCertificatesPage />,
  // HSE raporları (M13c; ADR 0049)
  complianceHseq: () => <HsePage type={null} />,
  complianceDeficiency: () => <HsePage type="deficiency" />,
  complianceNonConformity: () => <HsePage type="non_conformity" />,
  complianceAccident: () => <HsePage type="accident" />,
  complianceNearMiss: () => <HsePage type="near_miss" />,
  // Günlük iş planı ve günlük raporlar (M13e; ADR 0051): ofiste salt okunur
  operationsDailyPlan: () => <DailyPlanPage />,
  operationsDailyReports: () => <DailyReportsPage />,
  // Risk değerlendirmesi (M13d; ADR 0050)
  complianceRiskAssessment: () => <RiskAssessmentPage />,
  // Şirket RA kütüphanesi (K3; ADR 0087)
  complianceRiskLibrary: () => <RiskLibraryPage />,
  // Vessel Risk Register (K4; ADR 0088)
  complianceRiskRegister: () => <RiskRegisterPage />,
  // Operasyonel risk doğrulaması (K5; ADR 0089)
  complianceRiskVerification: () => <RiskVerificationPage />,
  // Bulgular ve düzeltici faaliyetler (K6; ADR 0094)
  complianceRiskFindings: () => <RiskFindingsPage />,
  // Risk panosu ve raporlar (K7; ADR 0095)
  complianceRiskDashboard: () => <RiskDashboardPage />,
  complianceRiskReports: () => <RiskReportsPage />,
  // Risk ana verisi (K2; ADR 0086)
  complianceRiskMasterData: () => <RiskMasterDataPage />,
};

/** Menüde olmayan alt sayfalar: dağıtım panosu (ADR 0019; plan M8a Task 17). */
const DISTRIBUTION_PATHS = [
  { section: 'ism', path: '/compliance/ism-forms/distribution' },
  { section: 'manuals', path: '/compliance/manuals/distribution' },
] as const;

/** Şirket kurulumu (ADR 0042 §22–§24): Settings altında sayfa içi; menüde değil. */
const SETUP_PAGES = [
  { path: PATHS.setupWizard, page: () => <SetupWizardPage /> },
  { path: PATHS.roles, page: () => <RolesPage /> },
  { path: PATHS.users, page: () => <UsersPage /> },
  { path: PATHS.matrices, page: () => <MatricesPage /> },
] as const;

/** Menünün her öğesi bir yol (menü `packages/ui/src/menu.ts`'te tek kaynak). */
function menuRoutes() {
  return menuLeaves().map(({ item, group }) => {
    const screen = SCREENS[item.id];
    return (
      <Route
        key={item.id}
        // Sekmeli öğe alt yollarını kendisi açar (ADR 0117 §14)
        path={item.nested ? `${item.path}/*` : item.path}
        element={
          <Guard stage="active">
            {screen && menuStage(item, 'web') === null ? (
              screen()
            ) : (
              <ComingSoonPage item={item} {...(group ? { group } : {})} />
            )}
          </Guard>
        }
      />
    );
  });
}

export function App() {
  return (
    <Routes>
      <Route
        path={PATHS.login}
        element={
          <Guard stage={null}>
            <LoginPage />
          </Guard>
        }
      />
      <Route
        path={PATHS.password}
        element={
          // Geçici parolada zorunlu; açık oturumda kendi parolasını değiştirme (ortak 2026-10-30)
          <Guard stage={PASSWORD_STAGES}>
            <PasswordPage />
          </Guard>
        }
      />
      <Route
        path={PATHS.totpSetup}
        element={
          <Guard stage="totp_enrollment_required">
            <TotpSetupPage />
          </Guard>
        }
      />
      <Route
        path={PATHS.totp}
        element={
          <Guard stage="totp_required">
            <TotpPage />
          </Guard>
        }
      />
      {menuRoutes()}
      {/* Kayıt sayfası (ADR 0042 §21): menüde değil, sayfa içi bağlantıyla */}
      <Route
        path={PATHS.formRecord}
        element={
          <Guard stage="active">
            <FormRecordPage />
          </Guard>
        }
      />
      {/* Crew Profile (ADR 0131 §2): Crew List'ten; menüde Crew List vurgulu */}
      <Route
        path="/crew/profile/:userId"
        element={
          <Guard stage="active">
            <CrewProfilePage />
          </Guard>
        }
      />
      {/* Setup Changes (ADR 0142 §3): Fleet'ten ve PMS şeridinden; menüde Vessels vurgulu */}
      <Route
        path={SETUP_CHANGES_PATH}
        element={
          <Guard stage="active">
            <SetupChangesPage />
          </Guard>
        }
      />
      <Route
        path="/compliance/manuals/folders"
        element={
          <Guard stage="active">
            <ManualsFoldersPage />
          </Guard>
        }
      />
      {SETUP_PAGES.map(({ path, page }) => (
        <Route key={path} path={path} element={<Guard stage="active">{page()}</Guard>} />
      ))}
      {DISTRIBUTION_PATHS.map(({ section, path }) => (
        <Route
          key={path}
          path={path}
          element={
            <Guard stage="active">
              <DistributionPage section={section} />
            </Guard>
          }
        />
      ))}
      <Route
        path="*"
        element={
          <Guard stage="active">
            <Navigate to={PATHS.dashboard} replace />
          </Guard>
        }
      />
    </Routes>
  );
}
